import { CountryPicker } from "@jugl-web/ui-components/web";
import { FC, useEffect, useMemo } from "react";
import { Controller, FormState, useForm } from "react-hook-form";
import { Button, TextInput } from "@jugl-web/ui-components/cross-platform";
import { cx, isValidEmail, useTranslations } from "@jugl-web/utils";
import { CountryPickerValue } from "@jugl-web/ui-components/web/CountryPicker/types";
import countries from "countries-phone-masks";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useEffectOnce } from "react-use";
import {
  getStorageItem,
  NEW_ENTITY_SESSION_PARAMS,
} from "@jugl-web/utils/storage";
import { MAX_WORKSPACE_NAME_LENGTH } from "@web-src/modules/auth/conts";
import { useDefaultCountry } from "@web-src/modules/cx/hooks/useDefaultCountry";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";

export type EntityFormValues = {
  name: string;
  email: string;
  country?: CountryPickerValue;
  imageFile?: File;
};

export type EntityFormProps = {
  defaultValues?: Pick<EntityFormValues, "name" | "email" | "imageFile"> & {
    countryIso: string;
  };
  defaultImgSrc?: string | null;
  canDeleteImage?: boolean;
  onSubmit: (values: EntityFormValues) => void;
  onFormStateChange?: (state: FormState<EntityFormValues>) => void;
};

export const CreateEntityForm: FC<EntityFormProps> = ({
  defaultValues,
  onSubmit,
  onFormStateChange,
}) => {
  const { usersApi } = useRestApiProvider();
  const { id: meId } = useMe();
  const defaultCountry = useDefaultCountry();
  const { register, handleSubmit, watch, control, formState, setValue } =
    useForm<EntityFormValues>({
      mode: "all",
      defaultValues: {
        ...defaultValues,
        country: countries.find(
          (country) => country.iso === defaultValues?.countryIso
        ),
      },
    });

  const { errors, isSubmitted } = formState;

  const { t } = useTranslations();
  const { data: userProfile } = usersApi.useGetUserProfileQuery(
    meId ? { params: { user_id: meId } } : {}
  );

  useEffect(() => {
    setValue(
      "name",
      userProfile ? `${userProfile.general.first_name}'s Workspace` : ""
    );
    setValue("country", defaultCountry);
  }, [defaultCountry, defaultValues, setValue, userProfile]);

  useEffect(() => {
    onFormStateChange?.(formState);
  }, [formState, onFormStateChange]);

  useEffectOnce(() => {
    const params:
      | {
          country?: string;
          name?: string;
          email?: string;
        }
      | undefined = getStorageItem(NEW_ENTITY_SESSION_PARAMS, sessionStorage);

    if (params) {
      setValue("name", params.name || "");
      setValue("email", params.email || "");
      setValue(
        "country",
        countries.find((c) => c.iso === params.country)
      );
    }
  });

  const selectedCountry = watch("country");

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit?.(values);
  });

  const allFieldsHaveValues =
    !!watch("name") && !!watch("email") && !!selectedCountry?.iso;

  const emailError = useMemo(() => {
    if (!errors.email || !isSubmitted) return undefined;
    if (watch("email")?.length === 0) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    return t({
      id: "form-controls.email.error-is-incorrect",
      defaultMessage: "Email is incorrect",
    });
  }, [errors.email, isSubmitted, t, watch]);

  return (
    <div className="flex max-w-[464px] flex-col items-center justify-center">
      <div className="flex w-[400px] flex-col items-center justify-center gap-8">
        <TextInput
          placeholder={t({
            id: "common.enter",
            defaultMessage: "Enter",
          })}
          classNames={{
            wrapperClassName: "w-full max-h-[100px]",
          }}
          label={t({
            id: "create-entity-form.workspace-name",
            defaultMessage: "Workspace Name",
          })}
          isRequired
          {...register("name", {
            required: true,
            validate: (value) => !!value.trim(),
            maxLength: {
              value: MAX_WORKSPACE_NAME_LENGTH,
              message: `Maximum character limit reached (${MAX_WORKSPACE_NAME_LENGTH})`,
            },
          })}
          errorMessage={errors.name?.message}
          isInvalid={!!errors.name?.message}
        />
        <TextInput
          autoFocus
          placeholder={t({
            id: "common.enter",
            defaultMessage: "Enter",
          })}
          classNames={{
            wrapperClassName: "w-full max-h-[100px]",
          }}
          label={t({
            id: "create-entity-form.workspace-email",
            defaultMessage: "Workspace Email",
          })}
          isRequired
          isInvalid={isSubmitted && !!errors.email}
          errorMessage={emailError}
          type="email"
          {...register("email", { required: true, validate: isValidEmail })}
        />
        <Controller
          control={control}
          name="country"
          rules={{
            required: {
              value: true,
              message: t({
                id: "form-controls.organization-country.error-is-required",
                defaultMessage: "Organization Country is required",
              }),
            },
          }}
          render={({ field }) => (
            <CountryPicker
              countryIso={field.value?.iso}
              onSelect={(item) => field.onChange(item)}
              placement="top"
              className="z-[9999]"
            >
              {(isCountryPickerOpen) => (
                <div className="relative">
                  <TextInput
                    value={
                      selectedCountry?.name
                        ? `${selectedCountry.name} (${selectedCountry.iso})`
                        : ""
                    }
                    isRequired
                    label={t({
                      id: "create-entity-form.workspace-country",
                      defaultMessage: "Workspace Country",
                    })}
                    placeholder={t({
                      id: "common.select",
                      defaultMessage: "Select",
                    })}
                    classNames={{
                      wrapperClassName: "w-[400px]",
                      inputClassName: "cursor-pointer",
                      endContentWrapperClassName: "bg-white",
                    }}
                    endContent={
                      <ChevronIcon
                        className={cx(
                          "absolute right-5 top-[52px] transform transition-transform",
                          isCountryPickerOpen && "rotate-180"
                        )}
                      />
                    }
                    isInvalid={!!errors.country}
                  />
                </div>
              )}
            </CountryPicker>
          )}
        />
      </div>
      <Button
        color="primary"
        isDisabled={!allFieldsHaveValues}
        className={cx("mt-8 w-[300px] 2xl:mt-16")}
        onClick={handleFormSubmit}
      >
        {t({
          id: "common.continue",
          defaultMessage: "Continue",
        })}
      </Button>
    </div>
  );
};
